/**
 * GA4 DataLayer v2 — ecommerce event utilities.
 *
 * Mandatory push pattern (GA4 best practice to prevent item-array bleed):
 *   window.dataLayer.push({ ecommerce: null });
 *   window.dataLayer.push({ event, ecommerce: { … } });
 */

import {I_AP_CATEGORY_ID_ENUM} from '@/_types/annual-programs'
import {SECTION_THEME_ENUM} from '@/_types/base'

import {saveGa4LastAmbulanceSlug} from './ga4-ambulance-slug.storage'

/** GA4 item_category values per CartProductType string. */
const TYPE_TO_CATEGORY: Record<string, string> = {
	'annual-program': 'Ročné programy',
	'annual-program-family': 'Ročné programy',
	'preventive-examination': 'Preventívne prehliadky',
	promotion: 'Akciová ponuka',
	'credit-program': 'Medante Kredit',
	template: 'Ambulancie',
	'price-list-item': 'Ambulancie',
}

export function getCategoryFromType(type: string): string {
	return TYPE_TO_CATEGORY[type] ?? ''
}

export interface GA4Item {
	item_id: string
	item_name: string
	item_brand: 'Medante'
	item_category: string
	item_category2?: string
	item_variant?: string
	item_list_id?: string
	item_list_name?: string
	index?: number
	price: number
	quantity: number
	discount?: number
	currency: 'EUR'
	coupon?: string
}

export interface BuildItemInput {
	/** Legacy numeric ID — for `template`, treated as `templateId` when that field is omitted. */
	id: number
	name: string
	/** CartProductType string, e.g. 'annual-program', 'preventive-examination'. */
	type: string
	/** null / undefined → treated as 0 (Medante Rodina calculator has price: null). */
	price: number | null | undefined
	priceOld?: number | null
	templateId?: number
	timeslotId?: number
	itemId?: number
	programId?: number
}

/** Explicit GA4 cart line input — preferred for order-flow ambulance products. */
export interface BuildGa4CartItemInput {
	type: string
	name: string
	price: number | null | undefined
	priceOld?: number | null
	templateId?: number
	timeslotId?: number
	itemId?: number
	programId?: number
	/** Legacy: when `type === 'template'` and `templateId` is missing, used as template id. */
	id?: number
}

export interface BuildItemOverrides {
	item_list_id?: string
	item_list_name?: string
	index?: number
	item_category2?: string
	item_variant?: string
	coupon?: string
	quantity?: number
}

/** List context for `select_item` and list-scoped item fields (§ 6.3, § 4.8). */
export interface GA4ListContext {
	item_list_id: string
	item_list_name: string
	index?: number
	item_category2?: string
	ambulanceSlug?: string
}

export interface GA4UserData {
	email: string
	phone: string | null
}

/**
 * § 4.4 — Ročné programy: Dospelí / Deti / Rodina (program tier, not patient gender).
 */
export function getAnnualProgramItemCategory2(input: {
	categoryId?: number
	sectionTheme?: SECTION_THEME_ENUM
	primaryCategory?: 'male' | 'female' | 'child'
	isFamily?: boolean
}): string {
	if (input.isFamily || input.categoryId === I_AP_CATEGORY_ID_ENUM.FAMILY) {
		return 'Rodina'
	}
	if (
		input.categoryId === I_AP_CATEGORY_ID_ENUM.CHILD ||
		input.sectionTheme === SECTION_THEME_ENUM.CHILD ||
		input.primaryCategory === 'child'
	) {
		return 'Deti'
	}
	return 'Dospelí'
}

/** § 4.4 — Preventívne prehliadky: Ženy / Muži from category slug. */
export function getPreventiveExaminationItemCategory2(slug: string): string {
	return slug === 'zeny' ? 'Ženy' : 'Muži'
}

/** Context for resolving § 4.4 `item_category2` on checkout / purchase. */
export interface Ga4CartAnalyticsContext {
	yearProgramCategoryId?: number
	preventiveCategorySlug?: 'zeny' | 'muzi'
	ambulanceName?: string
}

/**
 * § 4.4 — Maps cart product type + loaded API context to `item_category2`.
 */
export function resolveItemCategory2ForCartProduct(
	type: string,
	context: Ga4CartAnalyticsContext
): string | undefined {
	switch (type) {
		case 'annual-program-family':
			return 'Rodina'
		case 'annual-program':
			if (context.yearProgramCategoryId == null) return undefined
			return getAnnualProgramItemCategory2({
				categoryId: context.yearProgramCategoryId
			})
		case 'preventive-examination':
			return context.preventiveCategorySlug
				? getPreventiveExaminationItemCategory2(context.preventiveCategorySlug)
				: undefined
		case 'template':
		case 'price-list-item':
			return context.ambulanceName
		default:
			return undefined
	}
}

/** List price before discount when cart/checkout reported non-zero savings. */
export function priceOldFromPayableAndSavings(
	payable: number,
	savings: number
): number | undefined {
	return savings > 0 ? payable + savings : undefined
}

/** Maps backend `orderFormType` to GA4 cart / item_id type segment. */
export function normalizeGa4ProductType(orderFormType: string): string {
	switch (orderFormType) {
		case 'timeslot':
			return 'template'
		case 'priceListPromo':
			return 'promotion'
		case 'yearProgram':
			return 'annual-program'
		case 'preventiveExamination':
			return 'preventive-examination'
		case 'priceListItem':
			return 'price-list-item'
		case 'creditProgram':
			return 'credit-program'
		default:
			return orderFormType
	}
}

/** GA4 `item_id` numeric segment — must match `add_to_cart` (template uses template id, not timeslot). */
export function resolveGa4BuildItemId(
	type: string,
	data: {
		templateId?: number
		timeslotId?: number
		itemId?: number
		programId?: number
	}
): number {
	switch (type) {
		case 'template':
			return data.templateId ?? 0
		case 'price-list-item':
			return data.itemId ?? 0
		default:
			return data.programId ?? 0
	}
}

/**
 * Fires GA4 `view_item_list`. Returns false when skipped (SSR or empty items).
 * Callers should use a ref to avoid duplicate pushes within the same mount.
 */
export function pushViewItemListOnce(
	_itemListId: string,
	ecommerce: {
		item_list_id: string
		item_list_name: string
		items: GA4Item[]
	}
): boolean {
	if (typeof window === 'undefined' || ecommerce.items.length === 0) return false

	pushEvent('view_item_list', ecommerce)
	return true
}

export interface PushAddToCartOptions extends BuildItemOverrides {
	value?: number
	coupon?: string
	ambulanceSlug?: string
}

function resolveBuildItemNumericId(product: BuildItemInput): number {
	return resolveGa4BuildItemId(product.type, {
		templateId:
			product.templateId ?? (product.type === 'template' ? product.id : undefined),
		timeslotId: product.timeslotId,
		itemId: product.itemId ?? (product.type === 'price-list-item' ? product.id : undefined),
		programId: product.programId ?? product.id
	})
}

/** Builds a GA4 item with explicit id fields (template / timeslot / price-list / program). */
export function buildGa4CartItem(
	product: BuildGa4CartItemInput,
	overrides: BuildItemOverrides = {}
): GA4Item {
	const templateId =
		product.templateId ?? (product.type === 'template' ? product.id : undefined)

	return buildItem(
		{
			id: resolveGa4BuildItemId(product.type, {
				templateId,
				timeslotId: product.timeslotId,
				itemId: product.itemId,
				programId: product.programId
			}),
			name: product.name,
			type: product.type,
			price: product.price,
			priceOld: product.priceOld,
			templateId,
			timeslotId: product.timeslotId,
			itemId: product.itemId,
			programId: product.programId
		},
		{
			...overrides,
			...(product.type === 'template' &&
				product.timeslotId != null &&
				overrides.item_variant === undefined && {
					item_variant: String(product.timeslotId)
				})
		}
	)
}

/** Fires GA4 `add_to_cart` for a single product line. */
export function pushAddToCart(product: BuildItemInput, options: PushAddToCartOptions = {}): void {
	const {value, coupon, ambulanceSlug, ...overrides} = options
	if (ambulanceSlug) {
		saveGa4LastAmbulanceSlug(ambulanceSlug, overrides.item_category2)
	}
	const item = buildItem(product, overrides)
	pushEvent('add_to_cart', {
		currency: 'EUR',
		value: value ?? product.price ?? 0,
		...(coupon !== undefined && coupon !== '' && {coupon}),
		items: [item]
	})
}

export function pushSelectItem(product: BuildItemInput, list: GA4ListContext): void {
	if (list.ambulanceSlug) {
		saveGa4LastAmbulanceSlug(list.ambulanceSlug, list.item_category2)
	}
	pushEvent('select_item', {
		item_list_id: list.item_list_id,
		item_list_name: list.item_list_name,
		items: [
			buildItem(product, {
				item_list_id: list.item_list_id,
				item_list_name: list.item_list_name,
				index: list.index,
				item_category2: list.item_category2
			})
		]
	})
}

export function buildItem(product: BuildItemInput, overrides: BuildItemOverrides = {}): GA4Item {
	const price = product.price ?? 0
	const discount =
		product.priceOld != null && product.priceOld > price ? product.priceOld - price : undefined
	const numericId = resolveBuildItemNumericId(product)

	return {
		item_id: `${product.type}-${numericId}`,
		item_name: product.name,
		item_brand: 'Medante',
		item_category: getCategoryFromType(product.type),
		currency: 'EUR',
		price,
		quantity: overrides.quantity ?? 1,
		...(discount !== undefined && {discount}),
		...(overrides.item_list_id !== undefined && {item_list_id: overrides.item_list_id}),
		...(overrides.item_list_name !== undefined && {item_list_name: overrides.item_list_name}),
		...(overrides.index !== undefined && {index: overrides.index}),
		...(overrides.item_category2 !== undefined && {item_category2: overrides.item_category2}),
		...(overrides.item_variant !== undefined && {item_variant: overrides.item_variant}),
		...(overrides.coupon !== undefined && {coupon: overrides.coupon}),
	}
}

/**
 * Fire a GA4 ecommerce event.
 * Always prepends `{ ecommerce: null }` to clear stale items[] from previous events.
 */
export function pushEvent(event: string, ecommerce: Record<string, unknown>, user_data?: GA4UserData): void {
	if (typeof window === 'undefined') return
	window.dataLayer = window.dataLayer || []
	window.dataLayer.push({ecommerce: null, user_data: null})
	window.dataLayer.push({
		event, ecommerce, user_data: (user_data ? {
			email: btoa(user_data.email),
			phone: user_data.phone ? btoa(user_data.phone) : undefined
		} : undefined)
	})
}

/**
 * Fire a GA4 non-ecommerce event (generate_lead, outbound_click, …).
 * Does NOT prepend ecommerce: null.
 */
export function pushSimpleEvent(event: string, payload: Record<string, unknown>): void {
	if (typeof window === 'undefined') return
	window.dataLayer = window.dataLayer || []

	if (payload.user_data) {
		const user_data = payload.user_data as GA4UserData

		payload.user_data = {
			email: btoa(user_data.email),
			phone: user_data.phone ? btoa(user_data.phone) : undefined
		}
	}

	window.dataLayer.push({event, ...payload})
}
